<template>
  <section
    class="is-flex is-flex-direction-column has-background-secondary px-4 pb-2"
    :data-e2e="TimeEntryCypressAttribute.views.ADD_TIME_ENTRY_FORM"
  >
    <div class="is-flex is-full-width is-justify-content-space-between is-align-items-center">
      <div class="is-flex">
        <FormDate
          v-model="form.startedAt"
          :format="null"
          :show-float-label="false"
          :min-date="weekDates.length ? dayjs(weekDates[0]).toDate() : null"
          :max-date="maxDate"
          :data-e2e="TimeEntryCypressAttribute.inputs.DATE"
        >
          <template #trigger>
            <div
              class="is-flex is-align-items-center is-clickable calendar-button has-border-radius p-2"
            >
              <i class="cls cls-calendar is-size-5 p-r-5 has-text-primary" />
              <p class="is-size-6">
                {{ formDateTrigger }}
              </p>
            </div>
          </template>
        </FormDate>
      </div>

      <FormMultiSelect
        :config="selectConfig"
        v-model="form.duration"
        :show-float-label="false"
        :data-e2e="TimeEntryCypressAttribute.inputs.DURATION"
      />
    </div>

    <div class="is-flex is-full-width is-relative">
      <FormTextarea
        class="is-full-width"
        v-model="form.description"
        :placeholder="$t('timeentry.description')"
        :rows="1"
        :auto-height="true"
        :show-float-label="false"
        :data-e2e="TimeEntryCypressAttribute.inputs.DESCRIPTION"
      />
      <span class="is-absolute xy-r-10 xy-b-9" @click.stop="clearDescription">
        <i class="cls cls-remove-circle has-text-danger is-clickable is-size-5" />
      </span>
    </div>

    <div class="is-flex is-full-width is-align-items-center is-justify-content-space-between mt-2">
      <div
        class="is-flex is-align-items-center time-entry-used-ai-checkbox"
        :data-e2e="TimeEntryCypressAttribute.inputs.USED_AI"
      >
        <FormCheckbox v-model="form.isAiUsed" :label="$t('timeentry.i-used-ai-for-this-entry')" />
      </div>
      <button
        class="button is-primary is-size-5"
        @click="onSave"
        :data-e2e="TimeEntryCypressAttribute.buttons.SAVE_TIME_ENTRY"
      >
        {{ $t('core.save') }}
      </button>
    </div>
  </section>
</template>

<script>
import { cloneDeep } from 'lodash';
import { TimeEntry } from '@/modules/timeentry/models/TimeEntry';
import FormMultiSelect from '@/modules/core/form/components/FormMultiSelect.vue';
import { TimeEntryDurationSelectConfig } from '@/modules/timeentry/models/TimeEntryDurationSelectConfig';
import { PredefinedTimeEntryDuration } from '@/modules/timeentry/enums/PredefinedTimeEntryDuration';
import { HasCurrentEstateMixin } from '@/modules/core/app/mixins/HasCurrentEstateMixin';
import TimeEntryFormService from '@/modules/timeentry/services/TimeEntryFormService';
import ToastService from '@/modules/core/ui/services/ToastService';
import EventBus from '@/modules/core/app/helpers/EventBus';
import { TimeEntryEvent } from '@/modules/timeentry/constants/TimeEntryEvent';
import FormTextarea from '@/modules/core/form/components/FormTextarea.vue';
import FormDate from '@/modules/core/form/components/FormDate.vue';
import FormCheckbox from '@/modules/core/form/components/FormCheckbox.vue';
import {
  isToday,
  canonicalStartDay,
  createLocalDateFromUTCString
} from '@/modules/core/app/helpers/DateHelper';
import { TimeEntryCypressAttribute } from '@/modules/timeentry/constants/TimeEntryCypressAttribute';
import dayjs from 'dayjs';
import { DateFormat } from '@/modules/core/app/constants/date.constants';
import { v4 as uuidv4 } from 'uuid';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'AddTimeEntryForm',
  emits: ['on-save', 'on-save-completed', 'on-save-failed'],
  mixins: [HasCurrentEstateMixin],
  components: { FormDate, FormMultiSelect, FormTextarea, FormCheckbox },
  props: {
    userId: {
      type: Number,
      required: true
    },
    type: {
      type: String,
      required: true
    },
    subType: {
      type: String,
      default: null
    },
    initialDate: {
      type: Date,
      default: () => new Date()
    },
    newEntry: {
      type: TimeEntry
    },
    weekDates: {
      type: Array,
      default: () => []
    },
    customEntry: {
      type: TimeEntry
    }
  },
  static: {
    TimeEntryCypressAttribute,
    dayjs
  },
  local() {
    return {
      selectConfig: new TimeEntryDurationSelectConfig()
    };
  },
  data() {
    return {
      /**
       * @var {TimeEntry|BaseModel}
       */
      form: null
    };
  },
  computed: {
    formDateTrigger() {
      return isToday(this.form.startedAt) ? this.$t('date.today') : this.form.formattedStartedAt;
    },
    maxDate() {
      if (!this.weekDates.length || this.weekDates.includes(dayjs().format(DateFormat.ISO))) {
        return new Date();
      }

      return dayjs(this.weekDates.at(-1)).toDate();
    }
  },
  methods: {
    async onSave() {
      const form = cloneDeep(this.form);
      ToastService.show(
        this.$t('timeentry.entry-added', {
          time: form.durationFormatted
        })
      );

      try {
        form.startedAt = canonicalStartDay(this.form.startedAt);

        const promise = TimeEntryFormService.submitForm({ ...form, user: null });
        this.$emit('on-save', this.userId, form);
        const newForm = await promise;
        newForm.tempId = form.tempId;
        this.setForm(newForm);
        this.$emit('on-save-completed', this.userId, newForm);
        EventBus.signal(TimeEntryEvent.NEW_TIME_ENTRY, this.userId, form);
      } catch (e) {
        this.$emit('on-save-failed', this.userId, form);
      } finally {
        this.resetForm();
      }
    },
    setForm(form = {}) {
      const mergedPayload = this.customEntry
        ? { ...form, ...this.customEntry }
        : { ...form, tempId: uuidv4() };

      this.form = TimeEntry.mapModel(mergedPayload);

      this.form.userId = this.userId;
      this.form.startedAt = this.form.startedAt
        ? createLocalDateFromUTCString(this.form.startedAt)
        : this.initialDate;
      this.form.duration = this.form.duration || PredefinedTimeEntryDuration.FIVE.value;
      this.form.setType(this.type);
      this.form.subType = this.subType;
      this.form.meta.status = form.meta?.status ?? this.currentEstate?.status;
    },
    resetForm() {
      this.clearDescription();
      this.setForm(this.newEntry);
    },
    clearDescription() {
      this.form.description = null;
    }
  },
  created() {
    this.setForm(this.newEntry);
  },
  watch: {
    customEntry(newValue) {
      this.setForm(newValue);
    }
  }
};
</script>

<style scoped>
.calendar-button {
  &:hover {
    background-color: var(--ce-primary-light);
  }
}

:deep(.form-multi-select) {
  margin-bottom: 0 !important;
  margin-right: var(--ce--8px);
  width: var(--ce-90px);
}

:deep(.form-multi-select .multiselect__tags) {
  min-height: var(--ce-36px);
  text-align: right;
  padding-right: var(--ce-30px);
}

:deep(.form-multi-select .multiselect__select) {
  top: var(--ce--1px);
  font-size: var(--ce-10px);
  font-weight: var(--ce-weight-semibold);
}

:deep(.form-multi-select .multiselect__tags),
:deep(.form-multi-select .multiselect__input),
:deep(.form-multi-select .multiselect__single) {
  border: 0;
  background: transparent;
  font-size: var(--ce-13px);
  text-align: right;
}

:deep(.form-multi-select .multiselect__content) {
  font-size: var(--ce-13px);
}

:deep(.form-input) {
  margin-bottom: 0 !important;
}

:deep(.form-input textarea) {
  resize: none;
  font-size: var(--ce-size-5);
}

.time-entry-used-ai-checkbox :deep(.form-checkbox) {
  margin-top: 0 !important;
}
</style>
